import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import appointmentService from '../services/appointmentService';
import AppointmentModal from '../components/AppointmentModal';

const HOURS = Array.from({ length: 11 }, (_, i) => i + 8); // 8am a 6pm

const TYPE_LABELS = {
  firma: 'Firma',
  consulta: 'Consulta',
  entrega: 'Entrega',
  seguimiento: 'Seguimiento',
  interno: 'Interno',
};

const STATUS_COLORS = {
  programada: '#3b82f6',
  confirmada: '#10b981',
  realizada: '#6b7280',
  cancelada: '#ef4444',
  reprogramada: '#f59e0b',
};

const STATUS_LABELS = {
  programada: 'Programada',
  confirmada: 'Confirmada',
  realizada: 'Realizada',
  cancelada: 'Cancelada',
  reprogramada: 'Reprogramada',
};

function getWeekDays(referenceDate) {
  const date = new Date(referenceDate);
  const day = date.getDay(); // 0=domingo, 1=lunes...
  const monday = new Date(date);
  monday.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function formatDate(date) {
  return date.toISOString().split('T')[0];
}

function sameDay(d1, d2) {
  return formatDate(d1) === formatDate(d2);
}

export default function AgendaCalendar() {
  const navigate = useNavigate();
  const [view, setView] = useState('week'); // week | day | list
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [preselectedDate, setPreselectedDate] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUser, setFilterUser] = useState('');

  const weekDays = getWeekDays(currentDate);
  const dateFrom = formatDate(weekDays[0]);
  const dateTo = formatDate(weekDays[weekDays.length - 1]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (filterStatus) params.status = filterStatus;
      if (filterUser) params.user_id = filterUser;
      const res = await appointmentService.getAll(params);
      setAppointments(res.data.data ?? res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filterStatus, filterUser]);

  useEffect(() => { load(); }, [load]);

  const goToday = () => setCurrentDate(new Date());
  const goPrev = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - (view === 'day' ? 1 : 7));
    setCurrentDate(d);
  };
  const goNext = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + (view === 'day' ? 1 : 7));
    setCurrentDate(d);
  };

  const openCreate = (date = null) => {
    setEditingAppointment(null);
    setPreselectedDate(date);
    setShowModal(true);
  };

  const openEdit = (appt) => {
    setEditingAppointment(appt);
    setPreselectedDate(null);
    setShowModal(true);
  };

  const handleSaved = () => {
    setShowModal(false);
    load();
  };

  const getAppointmentsForDay = (date) =>
    appointments.filter(a => sameDay(new Date(a.start_datetime), date));

  const getAppointmentsForHour = (date, hour) =>
  appointments.filter(a => {
    const start = new Date(a.start_datetime);
    return sameDay(start, date) && start.getUTCHours() === hour;
  });

  const monthLabel = currentDate.toLocaleString('es-MX', { month: 'long', year: 'numeric' });

  return (
    <div className="container-fluid py-3">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <h4 className="mb-0 text-capitalize">{monthLabel}</h4>
          <button className="btn btn-sm btn-outline-secondary" onClick={goPrev}>‹</button>
          <button className="btn btn-sm btn-outline-secondary" onClick={goToday}>Hoy</button>
          <button className="btn btn-sm btn-outline-secondary" onClick={goNext}>›</button>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          {/* Filtro status */}
          <select className="form-control form-control-sm" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 140 }}>
            <option value="">Todos los status</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>

          {/* Vista */}
          <div className="btn-group btn-group-sm">
            {['week', 'day', 'list'].map(v => (
              <button key={v} className={`btn btn-outline-primary${view === v ? ' active' : ''}`} onClick={() => setView(v)}>
                {v === 'week' ? 'Semana' : v === 'day' ? 'Día' : 'Lista'}
              </button>
            ))}
          </div>

          <button className="btn btn-sm btn-primary" onClick={() => openCreate()}>
            + Nueva cita
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate('/agenda/stats')}>
            📊 Estadísticas
          </button>
        </div>
      </div>

      {loading && <div className="text-center py-4"><div className="spinner-border text-primary" /></div>}

      {/* Vista Semana */}
      {!loading && view === 'week' && (
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-bordered mb-0" style={{ minWidth: 800, tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ width: 60 }}></th>
                {weekDays.map((d, i) => {
                  const isToday = sameDay(d, new Date());
                  return (
                    <th key={i} className={`text-center${isToday ? ' bg-primary text-white' : ''}`} style={{ cursor: 'pointer' }} onClick={() => { setCurrentDate(d); setView('day'); }}>
                      <div style={{ fontSize: 12 }}>{d.toLocaleString('es-MX', { weekday: 'short' }).toUpperCase()}</div>
                      <div style={{ fontSize: 18, fontWeight: 'bold' }}>{d.getDate()}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {HOURS.map(hour => (
                <tr key={hour} style={{ height: 64 }}>
                  <td className="text-right text-muted" style={{ fontSize: 11, verticalAlign: 'top', paddingTop: 4 }}>
                    {hour}:00
                  </td>
                  {weekDays.map((d, di) => {
                    const dayAppts = getAppointmentsForHour(d, hour);
                    return (
                      <td key={di} style={{ verticalAlign: 'top', padding: 2, cursor: 'pointer' }}
                        onClick={() => openCreate(new Date(d.setHours(hour, 0, 0, 0)))}>
                        {dayAppts.map(a => (
                          <div key={a.id}
                            onClick={e => { e.stopPropagation(); navigate(`/agenda/${a.id}`); }}
                            style={{
                              background: STATUS_COLORS[a.status],
                              color: '#fff',
                              borderRadius: 4,
                              padding: '2px 5px',
                              marginBottom: 2,
                              fontSize: 11,
                              cursor: 'pointer',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                            }}
                            title={`${a.title} — ${a.user?.name}`}
                          >
                            {new Date(a.start_datetime).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} {a.title}
                          </div>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Vista Día */}
      {!loading && view === 'day' && (
        <div>
          <h5 className="mb-3 text-capitalize">
            {currentDate.toLocaleString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}
          </h5>
          <table className="table table-bordered" style={{ tableLayout: 'fixed' }}>
            <tbody>
              {HOURS.map(hour => {
                const dayAppts = getAppointmentsForHour(currentDate, hour);
                return (
                  <tr key={hour} style={{ height: 70 }}>
                    <td style={{ width: 70, verticalAlign: 'top', fontSize: 12, color: '#888', paddingTop: 6 }}>
                      {hour}:00
                    </td>
                    <td style={{ verticalAlign: 'top', padding: 4, cursor: 'pointer' }}
                      onClick={() => openCreate(new Date(new Date(currentDate).setHours(hour, 0, 0, 0)))}>
                      {dayAppts.map(a => (
                        <div key={a.id}
                          onClick={e => { e.stopPropagation(); navigate(`/agenda/${a.id}`); }}
                          style={{
                            background: STATUS_COLORS[a.status],
                            color: '#fff',
                            borderRadius: 6,
                            padding: '4px 10px',
                            marginBottom: 4,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div>
                            <strong style={{ fontSize: 13 }}>{a.title}</strong>
                            <span style={{ fontSize: 11, marginLeft: 8 }}>{TYPE_LABELS[a.type]}</span>
                          </div>
                          <div style={{ fontSize: 11, textAlign: 'right' }}>
                            <div>{a.user?.name}</div>
                            <div>{new Date(a.start_datetime).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} — {new Date(a.end_datetime).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}</div>
                          </div>
                        </div>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Vista Lista */}
      {!loading && view === 'list' && (
        <div>
          {weekDays.map((d, di) => {
            const dayAppts = getAppointmentsForDay(d);
            if (dayAppts.length === 0) return null;
            return (
              <div key={di} className="mb-4">
                <h6 className="text-capitalize text-muted mb-2">
                  {d.toLocaleString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}
                </h6>
                {dayAppts.map(a => (
                  <div key={a.id} className="card mb-2" style={{ cursor: 'pointer', borderLeft: `4px solid ${STATUS_COLORS[a.status]}` }}
                    onClick={() => navigate(`/agenda/${a.id}`)}>
                    <div className="card-body py-2 px-3 d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{a.title}</strong>
                        <span className="badge ml-2" style={{ background: STATUS_COLORS[a.status], color: '#fff' }}>
                          {STATUS_LABELS[a.status]}
                        </span>
                        <span className="text-muted ml-2" style={{ fontSize: 12 }}>{TYPE_LABELS[a.type]}</span>
                        {a.client && <span className="text-muted ml-2" style={{ fontSize: 12 }}>· {a.client.name}</span>}
                      </div>
                      <div className="text-right text-muted" style={{ fontSize: 12 }}>
                        <div>{new Date(a.start_datetime).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} — {new Date(a.end_datetime).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' , timeZone: 'UTC'})}</div>
                        <div>{a.user?.name}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
          {appointments.length === 0 && (
            <div className="text-center text-muted py-5">No hay citas en esta semana</div>
          )}
        </div>
      )}

      {/* Leyenda */}
      <div className="d-flex gap-3 mt-3 flex-wrap">
        {Object.entries(STATUS_LABELS).map(([k, v]) => (
          <span key={k} style={{ fontSize: 12 }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: STATUS_COLORS[k], marginRight: 4 }} />
            {v}
          </span>
        ))}
      </div>

      {showModal && (
        <AppointmentModal
          appointment={editingAppointment}
          preselectedDate={preselectedDate}
          onClose={() => setShowModal(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}